import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatWIBClock, formatWIBLongDate } from "@/lib/time";
import { Clock, Globe, ShieldCheck, ServerCog } from "lucide-react";

/**
 * Read-only system time widget. Server is the single source of truth.
 * Local timer only ticks the SECONDS forward for a smooth clock UI —
 * the initial value (and every refresh) comes from GET /api/system/time.
 */
export default function SystemTimePanel() {
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/system/time");
      setInfo(data);
      setNow(new Date(data.server_time));
    } catch {}
  };

  useEffect(() => {
    refresh();
    const clock = setInterval(() => setNow((prev) => prev ? new Date(prev.getTime() + 1000) : prev), 1000);
    const sync = setInterval(refresh, 60 * 1000); // resync every minute
    return () => { clearInterval(clock); clearInterval(sync); };
  }, []);

  const wibTime = formatWIBClock(now);
  const wibDate = now ? formatWIBLongDate(now) : "--";

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-md p-6" data-testid="system-time-panel">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[#0055FF]/10 text-[#0055FF]">
          <ServerCog className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight">Waktu &amp; Zona</h2>
          <p className="text-xs text-[#9CA3AF]">
            Sumber waktu resmi aplikasi adalah server. Semua timestamp mengikuti jam server.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#0A0A0A] text-white rounded-md p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Waktu Server
          </div>
          <div
            className="mt-2 font-mono-tab text-4xl font-black tracking-tight"
            data-testid="system-clock"
          >
            {wibTime}
          </div>
          <div className="mt-1 text-xs text-white/60" data-testid="system-date">
            {wibDate}
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] flex items-center gap-1">
            <Globe className="h-3 w-3" /> Timezone
          </div>
          <div className="mt-2 font-display text-2xl font-black tracking-tight" data-testid="system-timezone">
            {info?.timezone || "Asia/Jakarta"}
          </div>
          <div className="mt-1 text-xs text-[#4B5563]" data-testid="system-utc-offset">
            {info?.label || "WIB / UTC+07:00"}
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> Status
          </div>
          <div className="mt-2 font-display text-2xl font-black tracking-tight text-[#065F46]">
            Terkunci
          </div>
          <div className="mt-1 text-xs text-[#4B5563]">
            Timezone default: Asia/Jakarta. Jam server tidak dapat diubah dari aplikasi.
          </div>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-[#9CA3AF] leading-relaxed">
        Endpoint <code className="font-mono-tab text-[#0055FF]">GET /api/system/time</code> bersifat read-only.
        Frontend hanya menampilkan waktu yang berasal dari server; timer lokal hanya untuk animasi detik.
      </div>
    </section>
  );
}
